import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { ConversationMessageContentType } from '@/models/conversations/conversation';
import type { RespondedType, ResponseError } from '@/models/response';
import { addToast } from '@/stores/toast';
import { fetcher } from '@/utils/fetcher';
import { batchMessagesInfiniteQueryOptions } from './use-get-batch-messages';

export const batchMessageRequestSchema = z.object({
  Message: z.string(),
  MsgType: z.nativeEnum(ConversationMessageContentType),
  UserIds: z.number().array(),
  GroupIds: z.number().array(),
  TagIds: z.number().array(),
});

export type BatchMessageRequest = z.infer<typeof batchMessageRequestSchema>;

export const useSendBatchMessage = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation<RespondedType, ResponseError, BatchMessageRequest>({
    mutationKey: ['/SendBatchMessage'],
    mutationFn: (params) => fetcher('/SendBatchMessage', params, { validResponseCodes: [0] }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: batchMessagesInfiniteQueryOptions.queryKey });
    },
    onSettled: (data, error) =>
      addToast({
        type: data ? 'success' : 'error',
        message: data ? t('common.send-success') : error?.message || t('common.send-failed'),
      }),
  });
};
